import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, MessageSquare, Bell, TrendingUp, Users } from 'lucide-react';

const FundraiserSubmissionSuccess = ({ fundraiserId, onClose }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(1);

  useEffect(() => {
    // Simulate progress animation
    const timer = setTimeout(() => {
      if (progress < 100) {
        setProgress(prev => {
          const newProgress = prev + 10;
          if (newProgress >= 33 && newProgress < 66) setStep(2);
          if (newProgress >= 66) setStep(3);
          return newProgress;
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [progress]);

  const features = [
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Verification Started",
      description: "Our team is reviewing your fundraiser details",
      status: "In Progress",
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Story Optimization",
      description: "We'll help make your story more compelling",
      status: "Pending",
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Ready to Launch",
      description: "You'll be notified once your fundraiser is live",
      status: "Up Next",
      color: "text-green-600",
      bgColor: "bg-green-50"
    }
  ];

  const nextSteps = [
    {
      title: "Add Medical Documents",
      description: "Increase trust by uploading medical reports",
      time: "10-15 minutes",
      action: "Add Now",
      color: "border-l-4 border-blue-500"
    },
    {
      title: "Invite Friends & Family",
      description: "Get early supporters to boost your campaign",
      time: "5 minutes",
      action: "Invite",
      color: "border-l-4 border-green-500"
    },
    {
      title: "Set Up Bank Account",
      description: "Receive donations directly to your account",
      time: "15 minutes",
      action: "Setup",
      color: "border-l-4 border-purple-500"
    }
  ];

  const stats = [
    { icon: <Clock className="w-5 h-5" />, label: "Review Time", value: "24-48 Hours" },
    { icon: <TrendingUp className="w-5 h-5" />, label: "Success Rate", value: "98%" },
    { icon: <Users className="w-5 h-5" />, label: "Team Members", value: "3 Assigned" }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="relative p-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl">
          <div className="absolute top-6 right-6">
            <button
              onClick={() => navigate('/')} // Changed to navigate to home page
              className="text-white/90 hover:text-white bg-white/20 hover:bg-white/30 p-2 rounded-full transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Fundraiser Submitted Successfully! 🎉
              </h1>
              <p className="text-emerald-100 text-lg">
                Your request ID: <span className="font-mono font-bold bg-white/20 px-3 py-1 rounded-lg">{fundraiserId}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="p-8 border-b">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold text-gray-900">Review Progress</h2>
              <span className="text-lg font-bold text-emerald-600">{progress}%</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Step {step} of 3: {step === 1 ? 'Initial Review' : step === 2 ? 'Content Optimization' : 'Final Approval'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`p-5 rounded-xl border transition-all ${feature.bgColor} ${
                  step > index ? 'border-emerald-300' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${feature.bgColor} ${feature.color}`}>
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                    <div className="flex items-center mt-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        step > index 
                          ? 'bg-emerald-100 text-emerald-700'
                          : step === index
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {step > index ? '✓ Completed' : feature.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats & Updates */}
        <div className="p-8 border-b">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">What Happens Next</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-sm mb-3">
                  <div className="text-emerald-600">{stat.icon}</div>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Stay Updated</h3>
                <p className="text-gray-600 mb-4">
                  We'll send you email and SMS notifications at every step. Make sure to check your inbox regularly.
                </p>
                <div className="flex items-center space-x-4">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                    Enable SMS Updates
                  </button>
                  <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    View Timeline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Get Ready for Launch</h2>
          
          <div className="space-y-4 mb-8">
            {nextSteps.map((step, index) => (
              <div key={index} className={`p-5 bg-white border border-gray-200 rounded-xl ${step.color} hover:shadow-md transition-shadow`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                    <div className="flex items-center mt-3">
                      <Clock className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-xs text-gray-500">{step.time}</span>
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors text-sm font-medium">
                    {step.action}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center justify-center space-x-2"
            >
              <span>Go to Dashboard</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            
            <button
              onClick={() => window.open(`/fundraiser/${fundraiserId}/preview`, '_blank')}
              className="flex-1 px-6 py-4 bg-white border-2 border-gray-300 text-gray-800 rounded-xl hover:border-emerald-500 hover:text-emerald-600 transition-all font-semibold flex items-center justify-center space-x-2"
            >
              <span>Preview Fundraiser</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>

          {/* Support Info */}
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-amber-900">Need Help?</h4>
                <p className="text-sm text-amber-800 mt-1">
                  Our support team is available 24/7. Call us at <span className="font-semibold">1800-XXX-XXXX</span> or email <span className="font-semibold">support@fundraiser.com</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundraiserSubmissionSuccess;